import React, { createContext, useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { Profile } from "../assets/models/profile";
import { getAllStudentProfiles, practiceWords } from "../services/backend/api";
import type { Word } from "../services/backend/backendTypes";

type ContextType = {
  profiles: Profile[];
  selectedProfile: any;
  handleProfileSelect: (profile: any) => void;
  logout: () => void;
  words: Word[];
  currentWord: Word | null;
  getOrUpdateToNextWord: () => Promise<Word | undefined>;
};

const ProfileContext = createContext<ContextType | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [words, setWords] = useState<Word[]>([]);
  const [selectedWordIdx, setSelectedWordIdx] = useState<number>(0);
  const router = useNavigate();

  const handleFetchWords = async (studentId: number) => {
    const wordData = await practiceWords(studentId);
    if (wordData) {
      setWords(wordData);
      setSelectedWordIdx(0);
    }

    console.log("Fetched words:", wordData);

    return wordData;
  };

  const handleProfileSelect = async (selectedProfile: any) => {
    setSelectedProfile(selectedProfile);
    await handleFetchWords(selectedProfile.studentId);
    router("/profile");
  };

  const logout = () => {
    setSelectedProfile(null);
  };

  const getOrUpdateToNextWord = async () => {
    const nextIndex = selectedWordIdx + 1;

    if (nextIndex >= words.length) {
      // Reset to the first word if we reach the end
      const words = await handleFetchWords(selectedProfile.studentId);

      return words[0];
    }

    setSelectedWordIdx(nextIndex);
    return words[nextIndex];
  };

  useEffect(() => {
    setTimeout(async () => {
      const profileData: Profile[] =
        (await getAllStudentProfiles()) as Profile[];

      if (profileData) {
        setProfiles(profileData);
      }
    }, 15000);
  }, []);

  // redirect to profile page if a profile is not already selected
  useEffect(() => {
    if (window.location.pathname === "/teacher-dashboard") {
      return; // Don't redirect if on teacher dashboard
    }
    if (!selectedProfile) {
      router("/", { replace: true });
    }
  }, [router, selectedProfile]);

  return (
    <ProfileContext.Provider
      value={{
        selectedProfile,
        handleProfileSelect,
        getOrUpdateToNextWord,
        logout,
        words,
        profiles: profiles as Profile[],
        currentWord: words[selectedWordIdx] || null,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfileContext = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfileContext must be used within a ProfileProvider");
  }
  return context;
};
